import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, Eye, EyeSlash } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatApiError = (detail) => {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: 'BATI-BOYS',
    logo_url: '',
    primary_color: '#002FA7'
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`);
        setSettings(response.data);
      } catch (error) {
        
      }
    };
    fetchSettings();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        setError(detail || "Votre compte est en attente de validation par un administrateur.");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div 
        className="login-image" 
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_timesheet-hub-62/artifacts/b65npm70_Ba%CC%82timent%20BATI-BOYS%20et%20ve%CC%81hicules%20commerciaux.png)' }}
      />
      <div className="login-form-container">
        <div className="login-form">
          <div className="login-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt="Logo" 
                  style={{ height: '48px', objectFit: 'contain' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              ) : (
                <img 
                  src="/batiboys-logo.svg" 
                  alt="BATI-BOYS" 
                  style={{ height: '48px', objectFit: 'contain' }}
                />
              )}
              <span className="sidebar-logo" style={{ color: settings.primary_color, fontSize: '1.75rem' }}>
                {settings.company_name}
              </span>
            </div>
            <h1 className="login-title">Connexion</h1>
            <p className="login-subtitle">Accedez a votre console de gestion</p>
          </div>

          {error && (
            <div data-testid="login-error" style={{ 
              padding: '12px 16px', 
              backgroundColor: '#FFEBEE', 
              color: '#C62828',
              borderRadius: '2px',
              marginBottom: '20px',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ paddingRight: '48px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#5E5F66'
                  }}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '8px', backgroundColor: settings.primary_color }}
              disabled={loading}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <p style={{ marginTop: '24px', textAlign: 'center', color: '#5E5F66' }}>
            Pas encore de compte ?{' '}
            <Link to="/register" style={{ color: settings.primary_color, textDecoration: 'none', fontWeight: '600' }}>
              S'inscrire
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
